// Importing React and external libs
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

// Import momentjs
import moment from 'moment';

// Importing antd libs
import {
  Button,
  DatePicker,
  Input,
  message,
  Table,
  Tooltip,
} from 'antd';
import 'antd/es/button/style/css';
import 'antd/es/date-picker/style/css';
import 'antd/es/input/style/css';
import 'antd/es/message/style/css';
import 'antd/es/table/style/css';
import 'antd/es/tooltip/style/css';

// Importing antd icons
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';

// Importing helix hooks
import useLanguage from 'helix-hooks/language';

// Import helix modules
import { BACKEND_DATE_FORMAT } from 'helix-modules/sDate';

// Importing helix components
import Error from 'helix/ui/error';

// Importing components
import Action from './action';
import languages from './lng';

// Importing component style
import './style.scss';

const MAX_OBJECT_DEPTH = 10;
const { RangePicker } = DatePicker;

// Helper class to handle session storage
class Storage {
  /**
   * Constructor
   * @param {tableId} identificator for the table to session
   * @returns {void}
  **/
  constructor(tableId) {
    this.tableId = tableId;
  }

  /**
   * Get sorter or filters from window.sessionStorage
   * @param {string} type of data requested from window.sessionStorage
   * @returns {object} sorter or filters object for the page table
  **/
  get(type) {
    try {
      const data = this._unhash(
        JSON.parse(window.sessionStorage.getItem(this.tableId)) || {}
      );
      return (type && data[type]) || data || {};
    } catch (error) {
      return {};
    }
  }

  /**
   * Save the filters and sorter in to window.sessionStorage
   * @param {object} filter and sorter
   * @returns {void}
  **/
  save(obj) {
    window.sessionStorage.setItem(this.tableId, JSON.stringify(this._hash(obj)));
  }

  /**
   * Method check if a date string is valid given a format
   * @param {object}
   * dates {array} to be validated
   * format {string} format to be compared with
   * strictCheck {boolean} whether to check loosely or not
   * @returns {boolean} whether date strings are valid
  **/
  _isDate({ dates, format = BACKEND_DATE_FORMAT, strictCheck = true }) {
    return Array.isArray(dates) && dates.every((date) => moment(date, format, strictCheck).isValid());
  }

  /**
   * Helper to check whether each value is moment object
   * @param {object}
   * dates {array} to be tested
   * @returns {boolean} whether all elements are moment objects
  **/
  _isMoment({ dates }) {
    return Array.isArray(dates) && dates.length > 0 && dates.every((date) => moment.isMoment(date));
  }

  /**
   * Method to hash filters and sorters recursively
   * convert moment dates into strings
   * @param {object} obj to hash
   * @param {number} depth page recursion depth
   * @returns {object} hashed data
  **/
  _hash(obj, depth = 0) {
    if (depth > MAX_OBJECT_DEPTH) {
      console.log('WARNING: Max depth exceeded.');
      return obj;
    }
    if (typeof obj === 'object') {
      return Object.keys(obj).reduce((unhashed, key) => {
        unhashed[key] = (unhashed[key] && (unhashed[key]?.constructor?.toString().indexOf('Object') !== -1))
          ? this._hash(unhashed[key], depth + 1)
          : this._isMoment({ dates: unhashed[key] })
            ? unhashed[key].map((date) => date.format(BACKEND_DATE_FORMAT))
            : unhashed[key];
        return unhashed;
      }, obj);
    }
  }

  /**
   * Convert date strings back to moment objects
   * @param {object} hashed containing filters and sorter
   * @param {number} depth of the object nesting
   * @returns {object} with date fields converted to momentjs
  **/
  _unhash(obj, depth = 0) {
    if (depth > MAX_OBJECT_DEPTH) {
      console.log('WARNING: Max depth exceeded.');
      return obj;
    }
    if (typeof obj === 'object') {
      return Object.keys(obj).reduce((hashed, key) => {
        hashed[key] = (hashed[key] && hashed[key]?.constructor?.toString().indexOf('Object') !== -1)
          ? this._unhash(hashed[key], depth + 1)
          : this._isDate({ dates: hashed[key] })
            ? hashed[key].map((date) => moment(date))
            : hashed[key];
        return hashed;
      }, obj);
    }
  }
}

// Defining main component
const List = (props) => {
  const {
    actions,
    columns: initialColumns,
    context,
    dataField,
    dataCountField,
    defaultSort,
    disableDelete,
    deleteInfo,
    editInfo,
    expandable,
    name,
    rowKey,
    pageSize,
    showMorePath,
    showPagination,
    onEdit,
    onDelete,
    onLoad,
    onSelectedRows,
    parser,
    reader,
  } = props;
  // Instantiating Storage
  const Session = new Storage(name);

  // Loading component language
  const Language = useLanguage({ languages });

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [objects, setObjects] = useState([]);
  const [pagination, setPagination] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [statedColumns, setStatedColumns] = useState(undefined);

  let refreshTo = undefined; // timeout for debouncing loading table page
  let onLoadTo = undefined; // timeout for on load complete

  // From https://ant.design/components/table/#components-table-demo-head
  // You can set as ['ascend', 'descend', 'ascend'] to prevent sorter back to default status.
  initialColumns.forEach((col) => {
    if (col.sorter) col.sortDirections = ['ascend', 'descend', 'ascend'];
  });

  /**
   * On delete
   * @param {object} record
   * @return {void}
  **/
  const _delete = async (record) => {
    const res = await onDelete(record);
    if (res?.error) return message.error(res?.msg || Language.get('common', 'UNKNOW_ERROR'));
    await _load({ pagination });
  };

  /**
   * Helper that will load the data for the proper table page
   * @param {void}
   * @return {void} state change
  **/
  const _load = async ({ pagination }) => {
    setError(false);
    setLoading(true);
    setObjects(undefined);
    clearTimeout(refreshTo);
    const { filters, sorter } = Session.get();
    const { current, pageSize: pagPageSize } = pagination || {};
    const limit = pagPageSize || pageSize;
    refreshTo = setTimeout(async () => {
      const res = await reader({
        filters: filters || {},
        limit,
        offset: pageSize * ((current ?? 1) - 1),
        order: sorter ? `${sorter.order === 'descend' ? '-' : ''}${sorter.field}` : '',
      });
      if (res.error) return setError(Language.get(context, res.msg));
      setPagination({ ...pagination, total: res[dataCountField] });
      setSelectedRows([]);
      setObjects(await parser(res[dataField]));
      setLoading(false);
    }, 1000);
  };

  /**
   * Handle table change
   * @param {object} newPagination
   * @param {object} newFilter
   * @param {object} newSort
   * @return {void}
  **/
  const _onTableChange = (newPagination, newFilters, newSorter) => {
    Session.save({
      filters: newFilters,
      sorter: {
        field: newSorter.field,
        order: newSorter.order,
      },
    });
    _tableColumnIconState(statedColumns);
    _load({ pagination: newPagination });
  };

  /**
   * Handle table row selection
   * @param {void}
   * @return {Object} row selection property
  **/
  const _onTableRowSelection = () => {
    return {
      onChange: (_, selectedRows) => {
        setSelectedRows(selectedRows);
      },
      getCheckboxProps: (record) => {
        const disabled = record.readonly;
        return {
          disabled,
          title: disabled ? Language.get(context, 'UNAUTHORIZED') : undefined,
        };
      },
    };
  };

  /**
   * Helper to add default action column
   * @param {object} filterInput ref to input elm
   * @return {object} with the filterDropdown and extras
  **/
  const _tableColumnAction = (custom) => {
    return (custom || onDelete || onEdit)
      ? [{
        title: Language.get(context, 'ACTIONS'),
        render: (record) => (
          <Action
            context={context}
            custom={custom}
            deleteInfo={deleteInfo}
            disableDelete={disableDelete}
            editInfo={editInfo}
            record={record}
            onDelete={onDelete && (() => _delete(record))}
            onEdit={onEdit && (() => onEdit(record))}
          />
        ),
      }]
      : [];
  };

  const _tableColumnIconState = (columns) => {
    const { filters, sorter: sSorter } = Session.get();
    if (columns) {
      columns = columns.map((column) => {
        // set sorter initial icon state from session
        column.defaultSortOrder = undefined;
        const sorter = sSorter || defaultSort;
        if (sorter.field === column.dataIndex) {
          column.defaultSortOrder = sorter.order;
        }
        // set filter initial icon state from session
        column.filteredValue = null;
        Object.keys(filters || {}).forEach((field) => {
          if (column.dataIndex === field) {
            column.filteredValue = filters[field];
          }
        });
        return column;
      });
      setStatedColumns(columns);
    }
  };

  useEffect(() => {
    const columns = [
      ...initialColumns,
      ..._tableColumnAction(actions),
    ];
    _tableColumnIconState(columns);
    _load({ pagination: { page: 1, pageSize } });
  }, []);

  useEffect(() => {
    if (onSelectedRows) onSelectedRows(selectedRows);
  }, [selectedRows]);

  useEffect(() => {
    if (!(loading && refreshTo) && onLoad) {
      clearTimeout(onLoadTo);
      onLoadTo = setTimeout(onLoad, 500);
    }
  }, [loading]);

  return (
    <div className="list-wrapper">
      {error
        ? (<Error message={error} />)
        : (
          <Fragment>
            {statedColumns && (
              <Table
                align="center"
                bordered={false}
                columns={statedColumns}
                dataSource={objects}
                expandable={expandable}
                loading={loading}
                pagination={!showMorePath && (showPagination && pagination)}
                rowKey={rowKey}
                size="small"
                onChange={_onTableChange}
                rowSelection={onSelectedRows && _onTableRowSelection()}
              />
            )}
            {showMorePath && (
              <div className="show-more">
                <Tooltip title={Language.get(context, 'SHOW_MORE_INFO')}>
                  <Link to={showMorePath}><Button>{Language.get(context, 'SHOW_MORE')}</Button></Link>
                </Tooltip>
              </div>
            )}
          </Fragment>
        )
      }
    </div>
  );
};

/**
 * Helper to add date filter to table column
 * @param {object} filterInput ref to input elm
 * @return {object} with the filterDropdown and extras
**/
export const tableColumnFilterDate = () => {
  let filterInput = undefined;
  return {
    filterDropdown: ({ clearFilters, confirm, selectedKeys, setSelectedKeys }) => {
      return (
        <div className="list-search">
          <RangePicker
            ref={node => { filterInput = node; }}
            size="small"
            value={selectedKeys}
            onChange={(date) => setSelectedKeys(date)}
          />
          <div className="list-search-buttons">
            <Button size="small" type="primary" onClick={() => confirm()}>
              Search
            </Button>
            <Button size="small" onClick={() => { clearFilters(); confirm(); }}>
              Reset
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: (filtered) => tableGetFilteredIcon(filtered, 'filter'),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => filterInput.focus(), 100);
    }
  };
};

/**
 * Helper to add search filter to table column
 * @return {object} with the filterDropdown and extras
**/
export const tableColumnFilterSearch = () => {
  let filterInput = undefined;
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      return (
        <div className="list-search">
          <Input
            placeholder="Search by"
            ref={node => { filterInput = node; }}
            size="small"
            value={selectedKeys[0]}
            onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
            onPressEnter={(event) => { event.stopPropagation(); confirm(); }}
          />
          <Button size="small" type="primary" onClick={() => confirm()}>
            Search
          </Button>
          <Button size="small" onClick={() => { clearFilters(); confirm(); }}>
            Reset
          </Button>
        </div>
      );
    },
    filterIcon: filtered => tableGetFilteredIcon(filtered),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => {
        filterInput.input.focus();
        filterInput.input.select();
      }, 100);
    }
  };
};

/**
 * Helper that generate filtered column icon
 * @param {boolean} filtered
 * @param {string} type icon
 * @return {object} react content
**/
const tableGetFilteredIcon = (filtered = false, type = 'search') => {
  return {
    'filter': ({ className }) => <FilterOutlined className={className} />,
    'search': ({ className }) => <SearchOutlined className={className} />,
  }[type]({ className: filtered ? 'filtered' : '' });
};

List.propTypes = {
  actions: PropTypes.array,
  columns: PropTypes.array,
  context: PropTypes.string,
  dataField: PropTypes.string,
  dataCountField: PropTypes.string,
  defaultSort: PropTypes.object,
  disableDelete: PropTypes.bool,
  expandable: PropTypes.object,
  name: PropTypes.string,
  pageSize: PropTypes.number,
  rowKey: PropTypes.string,
  showMorePath: PropTypes.string,
  showPagination: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onLoad: PropTypes.func,
  onSelectedRows: PropTypes.func,
  parser: PropTypes.func,
  reader: PropTypes.func,
};

List.defaultProps = {
  columns: [],
  context: 'list',
  dataField: 'results',
  dataCountField: 'count',
  defaultSort: { field: 'id', order: 'descend' },
  disableDelete: false,
  pageSize: 20,
  rowKey: 'id',
  showPagination: true,
  onSelectedRows: undefined,
  parser: (objects) => objects,
  reader: () => ({ results: undefined }),
};

export default List;
